import React, { useCallback, useRef, useState } from 'react';
import { FC } from 'react';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import AlertTemplate, { AlertTypes } from '@components/Alert/Alert';
import useTranslations from '@hooks/useTranslation';
import ButtonTemplate from '@ui/Button/Button';
import Title from '@ui/SectionTitle/Title';
import { EMAIL_REGEX, FBQ_EVENTS } from '../../constants';
import { contactApi } from '../../services/contact.service';
import ContentTemplate from '../../ui/Content/Content';
import {
    Background,
    ErrorMessage,
    Form,
    GlobalErrorMessage,
    Input,
    SuccessMessage,
    Wrapper,
} from './ContactForm.elements';

export interface ContactFormProps {}

const CompanyCoursesContactFormTemplate: FC<ContactFormProps> = () => {
    const { t } = useTranslations();

    const nameInputRef = useRef<HTMLInputElement>();
    const emailInputRef = useRef<HTMLInputElement>();
    const phoneInputRef = useRef<HTMLInputElement>();
    const companyInputRef = useRef<HTMLInputElement>();
    const topicInputRef = useRef<HTMLInputElement>();
    const countInputRef = useRef<HTMLInputElement>();
    const sizeInputRef = useRef<HTMLInputElement>();
    const termInputRef = useRef<HTMLInputElement>();

    const [hasNameError, setHasNameError] = useState(false);
    const [hasPhoneError, setHasPhoneError] = useState(false);
    const [hasEmailError, setHasEmailError] = useState(false);
    const [hasTopicError, setHasTopicError] = useState(false);
    const [hasSentError, setHasSentError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSent, setIsSent] = useState(false);
    const [token, setToken] = useState(null);

    const handleClick = async () => {
        setHasNameError(false);
        setHasPhoneError(false);
        setHasEmailError(false);
        setHasTopicError(false);
        setHasSentError(false);
        setIsSent(false);

        let hasError = false;

        if (!nameInputRef?.current?.value || nameInputRef?.current?.value === '') {
            setHasNameError(true);
            hasError = true;
        }

        if (!phoneInputRef?.current?.value || phoneInputRef?.current?.value === '') {
            setHasPhoneError(true);
            hasError = true;
        }

        if (emailInputRef?.current?.value === '' || !EMAIL_REGEX.test(emailInputRef?.current?.value)) {
            setHasEmailError(true);
            hasError = true;
        }

        if (!topicInputRef?.current?.value || topicInputRef?.current?.value === '') {
            setHasTopicError(true);
            hasError = true;
        }

        if (hasError) {
            return;
        }

        try {
            setIsLoading(true);

            (window as any)?.fbq?.('track', FBQ_EVENTS.LEAD);

            await contactApi.companyCoursesOrder({
                name: nameInputRef?.current?.value,
                phone: phoneInputRef?.current?.value,
                email: emailInputRef?.current?.value,
                topic: topicInputRef?.current?.value,
                ...(companyInputRef?.current?.value !== '' && { company: companyInputRef?.current?.value }),
                ...(countInputRef?.current?.value !== '' && { participantCount: countInputRef?.current?.value }),
                ...(sizeInputRef?.current?.value !== '' && { numberOfDays: sizeInputRef?.current?.value }),
                ...(termInputRef?.current?.value !== '' && { date: termInputRef?.current?.value }),
                token,
            });

            setIsSent(true);
            setIsLoading(false);

            emailInputRef.current.value = '';
            nameInputRef.current.value = '';
            phoneInputRef.current.value = '';
            topicInputRef.current.value = '';
            companyInputRef.current.value = '';
            countInputRef.current.value = '';
            sizeInputRef.current.value = '';
            termInputRef.current.value = '';
        } catch {
            setHasSentError(true);
        }
    };

    const verifyRecaptchaCallback = useCallback((token) => {
        setToken(token);
    }, []);

    return (
        <Background>
            {hasSentError && <AlertTemplate type={AlertTypes.error} message={t('contact.error')} />}
            <ContentTemplate withPadding>
                <Wrapper>
                    <Form>
                        <Title alignLeft isBlack>
                            {t('companyCourses.contact.title')}
                        </Title>
                        <Input
                            ref={nameInputRef}
                            placeholder={t('companyCourses.contact.input.name.placeholder')}
                            className={`${hasNameError ? 'error' : ''}`}
                        />
                        {hasNameError && <ErrorMessage>{t('companyCourses.contact.input.name.error')}</ErrorMessage>}
                        <Input
                            ref={companyInputRef}
                            placeholder={t('companyCourses.contact.input.company.placeholder')}
                        />
                        <Input
                            ref={phoneInputRef}
                            placeholder={t('companyCourses.contact.input.phone.placeholder')}
                            className={`${hasPhoneError ? 'error' : ''}`}
                        />
                        {hasPhoneError && <ErrorMessage>{t('companyCourses.contact.input.phone.error')}</ErrorMessage>}
                        <Input
                            ref={emailInputRef}
                            placeholder={t('companyCourses.contact.input.mail.placeholder')}
                            className={`${hasEmailError ? 'error' : ''}`}
                        />
                        {hasEmailError && <ErrorMessage>{t('companyCourses.contact.input.mail.error')}</ErrorMessage>}
                        <Input
                            ref={topicInputRef}
                            placeholder={t('companyCourses.contact.input.topic.placeholder')}
                            className={`${hasTopicError ? 'error' : ''}`}
                        />
                        {hasTopicError && <ErrorMessage>{t('companyCourses.contact.input.topic.error')}</ErrorMessage>}
                        <Input ref={countInputRef} placeholder={t('companyCourses.contact.input.count.placeholder')} />
                        <Input ref={sizeInputRef} placeholder={t('companyCourses.contact.input.size.placeholder')} />
                        <Input ref={termInputRef} placeholder={t('companyCourses.contact.input.term.placeholder')} />
                        <GoogleReCaptcha onVerify={verifyRecaptchaCallback} />
                        {(hasNameError || hasPhoneError || hasEmailError || hasTopicError) && (
                            <GlobalErrorMessage>{t('contact.form.error')}</GlobalErrorMessage>
                        )}
                        {!isSent && (
                            <ButtonTemplate
                                isLoading={isLoading}
                                marginTop={30}
                                onClick={handleClick}
                                text={t('companyCourses.contact.button')}
                            />
                        )}
                        {isSent && <SuccessMessage>{t('contact.form.sent')}</SuccessMessage>}
                    </Form>
                </Wrapper>
            </ContentTemplate>
        </Background>
    );
};

export default CompanyCoursesContactFormTemplate;
